import { useEffect, type RefObject } from 'react';
import { clampHighlightZoom } from '@/utils/fileHighlights';
import { markingScrollHost } from '@/utils/marginRings';

export function useFilePreviewZoom(input: {
  enabled: boolean;
  zoom: number;
  scrollRef: RefObject<HTMLElement | null>;
  onZoom: (zoom: number, origin?: { x: number; y: number }) => void;
  onFit: () => void;
  onToggleHighlights?: () => void;
}) {
  const { enabled, zoom, scrollRef, onZoom, onFit, onToggleHighlights } = input;

  useEffect(() => {
    if (!enabled) return;

    const zoomAt = (next: number, clientX?: number, clientY?: number) => {
      const node = scrollRef.current;
      const host = node ? markingScrollHost(node) : null;
      const clamped = clampHighlightZoom(next);
      if (host && clientX != null && clientY != null) {
        const rect = host.getBoundingClientRect();
        onZoom(clamped, { x: clientX - rect.left, y: clientY - rect.top });
        return;
      }
      onZoom(clamped);
    };

    const onKey = (event: KeyboardEvent) => {
      const meta = event.metaKey || event.ctrlKey;
      if (meta && event.shiftKey && (event.key === 'H' || event.key === 'h')) {
        event.preventDefault();
        onToggleHighlights?.();
        return;
      }
      if (!meta) return;
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        zoomAt(zoom + 0.25);
        return;
      }
      if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        zoomAt(zoom - 0.25);
        return;
      }
      if (event.key === '0') {
        event.preventDefault();
        onFit();
      }
    };

    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * 0.008);
      zoomAt(zoom * factor, event.clientX, event.clientY);
    };

    let pinchStart = 0;
    let pinchZoom = zoom;
    const distance = (touches: TouchList) => {
      const a = touches[0];
      const b = touches[1];
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    };
    const midpoint = (touches: TouchList) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    });

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      pinchStart = distance(event.touches);
      pinchZoom = zoom;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2 || pinchStart < 1) return;
      event.preventDefault();
      const mid = midpoint(event.touches);
      zoomAt(pinchZoom * (distance(event.touches) / pinchStart), mid.x, mid.y);
    };
    const onTouchEnd = () => {
      pinchStart = 0;
    };

    let gestureZoom = zoom;
    const onGestureStart = (event: Event) => {
      if (isPreviewTextTarget(event.target)) return;
      event.preventDefault();
      gestureZoom = zoom;
    };
    const onGestureChange = (event: Event) => {
      if (isPreviewTextTarget(event.target)) return;
      event.preventDefault();
      const ge = event as Event & { scale?: number; clientX?: number; clientY?: number };
      zoomAt(gestureZoom * (ge.scale ?? 1), ge.clientX, ge.clientY);
    };
    const onGestureEnd = (event: Event) => {
      if (isPreviewTextTarget(event.target)) return;
      event.preventDefault();
    };

    const node = scrollRef.current;
    const host = node ? markingScrollHost(node) : null;
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    host?.addEventListener('touchstart', onTouchStart, { passive: true });
    host?.addEventListener('touchmove', onTouchMove, { passive: false });
    host?.addEventListener('touchend', onTouchEnd);
    host?.addEventListener('gesturestart', onGestureStart);
    host?.addEventListener('gesturechange', onGestureChange);
    host?.addEventListener('gestureend', onGestureEnd);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('wheel', onWheel, true);
      host?.removeEventListener('touchstart', onTouchStart);
      host?.removeEventListener('touchmove', onTouchMove);
      host?.removeEventListener('touchend', onTouchEnd);
      host?.removeEventListener('gesturestart', onGestureStart);
      host?.removeEventListener('gesturechange', onGestureChange);
      host?.removeEventListener('gestureend', onGestureEnd);
    };
  }, [enabled, zoom, scrollRef, onZoom, onFit, onToggleHighlights]);
}

function isPreviewTextTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(target.closest('.fp-text, .fp-text-view, .fp-docx, .fp-sheet'))
  );
}

export function applyAnchoredScroll(
  host: HTMLElement,
  origin: { x: number; y: number },
  ratio: number,
) {
  host.scrollLeft = (host.scrollLeft + origin.x) * ratio - origin.x;
  host.scrollTop = (host.scrollTop + origin.y) * ratio - origin.y;
}
